import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  FiChevronDown, FiUsers, FiEyeOff, FiFileText, FiShield,
  FiRefreshCw, FiDatabase, FiActivity, FiHeadphones
} from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { pageFaqs } from '../data/faqs';

const WhiteLabelWordPress = () => {
  useEffect(() => {
    window.scrollTo(0, 0);

    const title = "White-Label WordPress Management | ManagedSite.net";
    const description = "White-label WordPress management for agencies. Your agency keeps the client relationship while ManagedSite.net handles updates, security, backups, monitoring, and reporting behind your brand.";

    document.title = title;

    const updateMeta = (selector, attr, value) => {
      const el = document.querySelector(selector);
      if (el) el.setAttribute(attr, value);
    };

    updateMeta('meta[name="description"]', 'content', description);
    updateMeta('meta[property="og:title"]', 'content', title);
    updateMeta('meta[property="og:description"]', 'content', description);
    updateMeta('meta[property="twitter:title"]', 'content', title);
    updateMeta('meta[property="twitter:description"]', 'content', description);
  }, []);

  const [openFaq, setOpenFaq] = useState(null);

  const whiteLabelMeans = [
    {
      title: "You keep the client",
      text: "Your agency remains the client-facing provider. Clients call you, pay you, and see your brand on the work.",
      icon: FiUsers
    },
    {
      title: "We stay backstage",
      text: "ManagedSite.net works as your technical team unless you ask us to join a conversation.",
      icon: FiEyeOff
    },
    {
      title: "Reports in your brand",
      text: "Updates, monitoring, and maintenance reports are written so you can send them as your own.",
      icon: FiFileText
    },
    {
      title: "No client poaching",
      text: "We do not solicit your clients. The relationship stays with the agency that brought the website.",
      icon: FiShield
    }
  ];

  const included = [
    { title: "WordPress updates", text: "Core, plugin, and theme updates with review when something breaks.", icon: FiRefreshCw },
    { title: "Security and backups", text: "Vulnerability monitoring, hardening, and offsite backups.", icon: FiDatabase },
    { title: "Monitoring", text: "Uptime, SSL, domain, and DNS monitoring on eligible plans.", icon: FiActivity },
    { title: "Support and reporting", text: "White-label reports plus included support time on higher plans.", icon: FiHeadphones }
  ];

  const steps = [
    { title: "Review", text: "We look at the client website, hosting, plugins, and current technical condition." },
    { title: "Set the model", text: "Choose Essential, Managed, or Advanced Care, plus how we communicate with your team." },
    { title: "Work behind your brand", text: "We handle the WordPress work. Your agency stays the named provider." },
    { title: "Report and improve", text: "You get reports you can send, plus a clear path when a site needs more than routine care." }
  ];

  const faqs = pageFaqs['/agency-services/white-label-wordpress'];

  return (
    <div className="w-full">
      <section className="relative bg-[#FAF8F3] pt-24 pb-24 lg:pt-40 lg:pb-36 overflow-hidden">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
          <div className="text-sm font-bold tracking-[0.25em] text-[#D7B77A] uppercase mb-8">Agency Services</div>
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-extrabold text-[#17211E] tracking-tight leading-[1.1] mb-10 max-w-4xl mx-auto">
            WordPress Management Behind Your Brand
          </h1>
          <p className="text-xl sm:text-2xl text-[#17211E]/80 leading-relaxed mb-12 max-w-3xl mx-auto font-medium">
            Your agency stays the client-facing provider. ManagedSite.net handles updates, security, backups, monitoring, and reporting as your technical team.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
            <Link to="/agency-plans" className="home-button">
              View Website Care Plans
            </Link>
            <Link to="/contact" className="home-button-secondary">
              Discuss an Agency Partnership
            </Link>
          </div>
          <p className="text-lg text-[#17211E]/50 font-bold italic">
            Your agency owns the client relationship. We work behind the scenes unless otherwise agreed.
          </p>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">What White-Label Means Here</h2>
            <p className="text-xl text-[#17211E]/70 leading-relaxed font-medium">
              This is not a resale plugin or a shared inbox. It is WordPress care delivered so your agency remains the named provider.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {whiteLabelMeans.map((item) => (
              <div key={item.title} className="bg-[#FAF8F3] p-10 rounded-3xl border border-[#EAF1EC] flex flex-col h-full">
                <div className="text-[#174C3C] mb-8">
                  <SafeIcon icon={item.icon} className="w-10 h-10" />
                </div>
                <h3 className="text-2xl font-bold text-[#17211E] mb-4">{item.title}</h3>
                <p className="text-lg text-[#17211E]/75 leading-relaxed font-medium">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#EAF1EC]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-6 leading-tight">White-Label Website Care Plans</h2>
            <p className="text-xl text-[#17211E]/75 font-medium">
              Care plans start at $59 per WordPress site each month. Volume pricing starts at 10 active eligible sites. We don't publish the full rate card publicly so you can set your own client pricing and protect your margin — partner rates are sent after a short review.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-12">
            {['Essential Care', 'Managed Care', 'Advanced Care'].map((plan) => (
              <div key={plan} className="bg-white p-10 rounded-3xl border border-[#3B7C6D]/10 text-center shadow-sm">
                <h3 className="text-xl font-bold text-[#17211E] mb-4">{plan}</h3>
                <div className="text-sm font-bold text-[#17211E]/40 uppercase tracking-widest mb-4">Per site</div>
                <div className="text-[#3B7C6D] font-extrabold text-sm">
                  Volume rate at 10+ sites
                </div>
              </div>
            ))}
          </div>
          <p className="text-center text-[#17211E]/60 font-medium mb-10 max-w-3xl mx-auto">
            Full feature comparison, support blocks, and volume rules are on Agency Plans. The technical stack is on Website Management.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/agency-plans" className="home-button">
              Compare Care Plans
            </Link>
            <Link to="/contact" className="home-button-secondary">
              Request an Agency Review
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#FAF8F3]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-20">
            <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-8 leading-tight">What We Handle on Each Site</h2>
            <p className="text-xl text-[#17211E]/70 leading-relaxed font-medium">
              The work is the same WordPress care we provide on managed sites. The difference is who the client sees.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
            {included.map((item) => (
              <div key={item.title} className="bg-white p-10 rounded-[2rem] border border-[#EAF1EC] shadow-sm flex gap-6">
                <div className="bg-[#EAF1EC] w-14 h-14 rounded-2xl flex items-center justify-center text-[#174C3C] flex-shrink-0">
                  <SafeIcon icon={item.icon} className="w-7 h-7" />
                </div>
                <div>
                  <h3 className="text-2xl font-extrabold text-[#17211E] mb-3">{item.title}</h3>
                  <p className="text-lg text-[#17211E]/70 leading-relaxed font-medium">{item.text}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center">
            <Link to="/services/website-management" className="text-[#174C3C] font-extrabold hover:text-[#3B7C6D] transition-colors text-xl inline-flex items-center gap-2">
              See the full website management stack
            </Link>
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl md:text-5xl font-bold text-[#17211E] mb-20 text-center">How White-Label Delivery Works</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {steps.map((step, i) => (
              <div key={step.title} className="relative">
                <div className="text-6xl font-black text-[#EAF1EC] mb-8">{i + 1}</div>
                <h3 className="text-2xl font-bold text-[#17211E] mb-5">{step.title}</h3>
                <p className="text-lg text-[#17211E]/75 leading-relaxed font-medium">{step.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-[#FAF8F3] border-t border-[#EAF1EC]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#17211E] mb-16 text-center">Frequently Asked Questions</h2>
          <div className="space-y-4">
            {faqs.map((faq, i) => (
              <div key={faq.q} className="bg-white border border-[#EAF1EC] rounded-2xl overflow-hidden shadow-sm">
                <button
                  onClick={() => setOpenFaq(openFaq === i ? null : i)}
                  aria-expanded={openFaq === i}
                  aria-controls={`faq-answer-${i}`}
                  className="w-full flex justify-between items-center p-8 text-left hover:bg-[#FAF8F3] transition-colors"
                >
                  <span className="text-xl font-bold text-[#17211E]">{faq.q}</span>
                  <SafeIcon icon={FiChevronDown} className={`w-6 h-6 text-[#3B7C6D] transition-transform ${openFaq === i ? 'rotate-180' : ''}`} />
                </button>
                <div
                  id={`faq-answer-${i}`}
                  className={`px-8 pb-8 text-xl text-[#17211E]/75 font-medium leading-relaxed ${openFaq === i ? 'block' : 'hidden'}`}
                >
                  {faq.a}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-bold text-[#17211E] mb-16 text-center">Related Pages</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { label: 'Website Management', link: '/services/website-management' },
              { label: 'Agency Plans', link: '/agency-plans' },
              { label: 'Agency Overview', link: '/agency-services' },
              { label: 'White-Label Support', link: '/agency-services/white-label-support' }
            ].map((item) => (
              <Link
                key={item.label}
                to={item.link}
                className="bg-[#FAF8F3] p-8 rounded-2xl border border-[#EAF1EC] font-bold text-[#17211E] text-center hover:shadow-md transition-all"
              >
                {item.label}
              </Link>
            ))}
          </div>
        </div>
      </section>

      <section className="py-32 bg-[#174C3C] text-center relative overflow-hidden text-white">
        <div className="absolute top-0 left-0 w-64 h-64 bg-white/5 rounded-full -translate-x-1/2 -translate-y-1/2"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-white/5 rounded-full translate-x-1/3 translate-y-1/3"></div>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <h2 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-10 tracking-tight leading-tight">
            Offer WordPress Care Without Building a Bench
          </h2>
          <p className="text-2xl text-[#FAF8F3]/80 mb-14 max-w-3xl mx-auto leading-relaxed font-medium">
            Keep the client relationship. Let ManagedSite.net handle the WordPress work behind your brand.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/agency-plans" className="home-button-on-dark">
              View Agency Plans
            </Link>
            <Link to="/contact" className="home-button-on-dark-secondary">
              Discuss an Agency Partnership
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WhiteLabelWordPress;
